import { useState } from "react";
import {
  FolderKanban, Plus, X, ChevronRight, ChevronDown, Globe, Trash2, Check,
  Target, Brain, TrendingUp, Zap, Settings, Radio, Clock,
  Trophy, Users, MessageSquare, FileText, Shield, Upload, Filter,
  BarChart3, Wrench
} from "lucide-react";

const NAV_GROUPS = [
  {
    label: "Analytics",
    items: [
      { id: "leaderboard", label: "Leaderboard", icon: Trophy },
      { id: "marketers", label: "Marketers", icon: Users },
      { id: "report", label: "Daily Report", icon: FileText },
    ],
  },
  {
    label: "Data",
    items: [
      { id: "entries", label: "Posts & Comments", icon: MessageSquare },
      { id: "filtered", label: "Filtered Posts", icon: Filter, adminOnly: false, hideClient: true },
    ],
  },
  {
    label: "Tools",
    items: [
      { id: "keywords", label: "Keywords", icon: TrendingUp, hideClient: true },
      { id: "content", label: "AI Content", icon: Brain },
      { id: "checklist", label: "Checklist", icon: Target },
      { id: "bridge", label: "Browser Control", icon: Radio, adminOnly: true },
      { id: "schedule", label: "Schedule", icon: Clock, adminOnly: true },
    ],
  },
  {
    label: "Admin",
    items: [
      { id: "admin", label: "Manage Marketers", icon: Shield, adminOnly: true },
      { id: "settings", label: "Project Settings", icon: Settings, adminOnly: true },
      { id: "upload", label: "Import", icon: Upload, hideClient: true },
      { id: "health", label: "Health", icon: Zap, adminOnly: true },
      { id: "app-settings", label: "App Settings", icon: Wrench, adminOnly: true },
    ],
  },
];

export default function ProjectSidebar({
  projects, selectedProject, onSelectProject, onCreateProject, onDeleteProject,
  collapsed, onToggle, isAdmin, isClient,
  activeTab, onTabChange,
}) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", display_name: "", website: "", keywords: "", description: "" });
  const [creating, setCreating] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const handleCreate = async () => {
    if (!form.name.trim() || !form.display_name.trim()) return;
    setCreating(true);
    await onCreateProject({
      ...form,
      keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
    });
    setForm({ name: "", display_name: "", website: "", keywords: "", description: "" });
    setShowForm(false);
    setCreating(false);
  };

  const toggleGroup = (label) => {
    setCollapsedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const filterItems = (items) =>
    items.filter((item) => {
      if (item.adminOnly && !isAdmin) return false;
      if (item.hideClient && isClient) return false;
      return true;
    });

  return (
    <div className={`project-sidebar ${collapsed ? "collapsed" : ""}`} data-testid="project-sidebar">
      <div className="sidebar-header">
        {!collapsed && (
          <div className="sidebar-brand" data-testid="sidebar-brand">
            <div className="sidebar-brand-icon">R</div>
            <span className="sidebar-brand-text">RedTracker</span>
          </div>
        )}
        <button className="sidebar-toggle" onClick={onToggle} data-testid="sidebar-toggle">
          <ChevronRight size={16} className={collapsed ? "" : "rotated"} />
        </button>
      </div>

      {!collapsed && (
        <div className="sidebar-body">
          {/* Project selector */}
          <div className="sidebar-section" data-testid="sidebar-projects">
            <div className="sidebar-section-header" onClick={() => toggleGroup("projects")}>
              <FolderKanban size={13} />
              <span>Projects</span>
              {collapsedGroups["projects"] ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
            </div>
            {!collapsedGroups["projects"] && (
              <div className="sidebar-section-items">
                {!isClient && (
                  <button
                    className={`sidebar-nav-item ${!selectedProject ? "active" : ""}`}
                    onClick={() => onSelectProject("")}
                    data-testid="project-all"
                  >
                    <BarChart3 size={14} />
                    <span>All Projects</span>
                  </button>
                )}
                {projects.map((p) => (
                  <div key={p.id} className={`sidebar-nav-item ${selectedProject === p.id ? "active" : ""}`}>
                    <button
                      className="sidebar-nav-item-btn"
                      onClick={() => onSelectProject(p.id)}
                      data-testid={`project-${p.id}`}
                    >
                      <Globe size={14} />
                      <span>{p.display_name || p.name}</span>
                    </button>
                    {isAdmin && (
                      <button
                        className="sidebar-nav-delete"
                        onClick={(e) => { e.stopPropagation(); onDeleteProject(p.id); }}
                        data-testid={`project-delete-${p.id}`}
                      >
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>
                ))}
                {isAdmin &&
                  (showForm ? (
                    <div className="sidebar-mini-form" data-testid="project-form">
                      <input placeholder="ID (e.g. logkeys)" value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })} className="sidebar-input" data-testid="project-form-name" />
                      <input placeholder="Display Name" value={form.display_name}
                        onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="sidebar-input" />
                      <div className="sidebar-form-actions">
                        <button className="btn btn-xs btn-primary" onClick={handleCreate} disabled={creating} data-testid="project-form-submit">
                          <Check size={12} /> Create
                        </button>
                        <button className="btn btn-xs btn-secondary" onClick={() => setShowForm(false)}>
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button className="sidebar-add-btn" onClick={() => setShowForm(true)} data-testid="add-project-btn">
                      <Plus size={13} /> New Project
                    </button>
                  ))}
              </div>
            )}
          </div>

          {/* Navigation groups */}
          {NAV_GROUPS.map((group) => {
            const items = filterItems(group.items);
            if (items.length === 0) return null;
            const isGroupCollapsed = collapsedGroups[group.label];
            return (
              <div key={group.label} className="sidebar-section" data-testid={`sidebar-group-${group.label.toLowerCase()}`}>
                <div className="sidebar-section-header" onClick={() => toggleGroup(group.label)}>
                  <span>{group.label}</span>
                  {isGroupCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                </div>
                {!isGroupCollapsed && (
                  <div className="sidebar-section-items">
                    {items.map((item) => (
                      <button
                        key={item.id}
                        className={`sidebar-nav-item ${activeTab === item.id ? "active" : ""}`}
                        onClick={() => onTabChange(item.id)}
                        data-testid={`nav-${item.id}`}
                      >
                        <item.icon size={14} />
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
